/**
 * Domain-specific status types for ArendaPro+
 * 
 * This file contains TypeScript types for the new domain-specific tables:
 * - order_statuses, rental_statuses, payment_methods, payment_statuses
 * - inventory_statuses, inventory_conditions
 * 
 * Following STYLEGUIDE.md:
 * - Frontend types use camelCase
 * - Database types use snake_case
 * - Mappers handle conversion between layers
 */

// =============================================================================
// BASE TYPES
// =============================================================================

type BaseEntity = {
  id: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
}

type BaseDbEntity = {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// ORDER STATUSES
// =============================================================================

export type OrderStatus = BaseEntity & {
  name: string;
  color: string;
  description?: string;
  sortOrder: number;
}

export type OrderStatusDb = BaseDbEntity & {
  name: string;
  color: string;
  description?: string | null;
  sort_order: number;
}

export type OrderStatusInsert = {
  name: string;
  color?: string;
  description?: string;
  user_id: string;
  sort_order?: number;
}

export type OrderStatusUpdate = {
  name?: string;
  color?: string;
  description?: string;
  sort_order?: number;
}

// =============================================================================
// RENTAL STATUSES
// =============================================================================

export type RentalStatus = BaseEntity & {
  name: string;
  color: string;
  description?: string;
  sortOrder: number;
}

export type RentalStatusDb = BaseDbEntity & {
  name: string;
  color: string;
  description?: string | null;
  sort_order: number;
}

export type RentalStatusInsert = {
  name: string;
  color?: string;
  description?: string;
  user_id: string;
  sort_order?: number;
}

export type RentalStatusUpdate = {
  name?: string;
  color?: string;
  description?: string;
  sort_order?: number;
}

// =============================================================================
// PAYMENT METHODS
// =============================================================================

export type PaymentMethod = BaseEntity & {
  name: string;
  description?: string;
  isActive: boolean;
  isDefault: boolean;
  config: Record<string, unknown>;
}

export type PaymentMethodDb = BaseDbEntity & {
  name: string;
  description?: string | null;
  is_active: boolean;
  is_default: boolean;
  config: Record<string, unknown>;
}

export type PaymentMethodInsert = {
  name: string;
  description?: string;
  is_active?: boolean;
  is_default?: boolean;
  config?: Record<string, unknown>;
  user_id: string;
}

export type PaymentMethodUpdate = {
  name?: string;
  description?: string;
  is_active?: boolean;
  is_default?: boolean;
  config?: Record<string, unknown>;
}

// =============================================================================
// PAYMENT STATUSES
// =============================================================================

export type PaymentStatus = BaseEntity & {
  name: string;
  color: string;
  description?: string;
  sortOrder: number;
}

export type PaymentStatusDb = BaseDbEntity & {
  name: string;
  color: string;
  description?: string | null;
  sort_order: number;
}

export type PaymentStatusInsert = {
  name: string;
  color?: string;
  description?: string;
  user_id: string;
  sort_order?: number;
}

export type PaymentStatusUpdate = {
  name?: string;
  color?: string;
  description?: string;
  sort_order?: number;
}

// =============================================================================
// INVENTORY STATUSES
// =============================================================================

export type InventoryStatus = BaseEntity & {
  name: string;
  color: string;
  description?: string;
  sortOrder: number;
}

export type InventoryStatusDb = BaseDbEntity & {
  name: string;
  color: string;
  description?: string | null;
  sort_order: number;
}

export type InventoryStatusInsert = {
  name: string;
  color?: string;
  description?: string;
  user_id: string;
  sort_order?: number;
}

export type InventoryStatusUpdate = {
  name?: string;
  color?: string;
  description?: string;
  sort_order?: number;
}

// =============================================================================
// INVENTORY CONDITIONS
// =============================================================================

export type InventoryCondition = BaseEntity & {
  name: string;
  color: string;
  description?: string;
  sortOrder: number;
}

export type InventoryConditionDb = BaseDbEntity & {
  name: string;
  color: string;
  description?: string | null;
  sort_order: number;
}

export type InventoryConditionInsert = {
  name: string;
  color?: string;
  description?: string;
  user_id: string;
  sort_order?: number;
}

export type InventoryConditionUpdate = {
  name?: string;
  color?: string;
  description?: string;
  sort_order?: number;
}

// =============================================================================
// WORKSHOP STATUSES
// =============================================================================

export type WorkshopStatus = BaseEntity & {
  name: string;
  color: string;
  description?: string;
  sortOrder: number;
}

export type WorkshopStatusDb = BaseDbEntity & {
  name: string;
  color: string;
  description?: string | null;
  sort_order: number;
}

export type WorkshopStatusInsert = {
  name: string;
  color?: string;
  description?: string;
  user_id: string;
  sort_order?: number;
}

export type WorkshopStatusUpdate = {
  name?: string;
  color?: string;
  description?: string;
  sort_order?: number;
}

// =============================================================================
// UNION TYPES FOR SELECTORS
// =============================================================================

export type DomainStatus = 
  | OrderStatus 
  | RentalStatus 
  | PaymentMethod
  | PaymentStatus 
  | InventoryStatus 
  | InventoryCondition
  | WorkshopStatus;

export type DomainStatusDb = 
  | OrderStatusDb 
  | RentalStatusDb 
  | PaymentStatusDb 
  | InventoryStatusDb 
  | InventoryConditionDb
  | WorkshopStatusDb;

// =============================================================================
// DOMAIN TYPE IDENTIFIERS
// =============================================================================

export type DomainType = 
  | 'order_status'
  | 'rental_status' 
  | 'payment_method'
  | 'payment_status'
  | 'inventory_status'
  | 'inventory_condition'
  | 'workshop_status';

// =============================================================================
// SELECTOR OPTIONS
// =============================================================================

export type DomainStatusOption = {
  id: string;
  name: string;
  color?: string;
  value: string;
  label: string;
}

export type DomainStatusFilters = {
  search?: string;
  isActive?: boolean;
}

// =============================================================================
// COMMON DOMAIN STATUS NAMES (for validation and defaults)
// =============================================================================

export const DEFAULT_ORDER_STATUSES = [
  'Активный',
  'К сборке', 
  'К сдаче',
  'Просрочен'
] as const;

export const DEFAULT_RENTAL_STATUSES = [
  'К сборке',
  'Активный',
  'К сдаче', 
  'Просрочен'
] as const;

export const DEFAULT_PAYMENT_METHODS = [
  'Наличные',
  'Карта',
  'Перевод'
] as const;

export const DEFAULT_PAYMENT_STATUSES = [
  'Оплачено',
  'Предоплата', 
  'Не оплачено'
] as const;

export const DEFAULT_INVENTORY_STATUSES = [
  'Доступен',
  'В аренде',
  'В ремонте',
  'Списан'
] as const;

export const DEFAULT_INVENTORY_CONDITIONS = [
  'Отличное',
  'Хорошее',
  'Нормальное',
  'Плохое',
  'Повреждённое'
] as const;

export type DefaultOrderStatus = typeof DEFAULT_ORDER_STATUSES[number];
export type DefaultRentalStatus = typeof DEFAULT_RENTAL_STATUSES[number];
export type DefaultPaymentMethod = typeof DEFAULT_PAYMENT_METHODS[number];
export type DefaultPaymentStatus = typeof DEFAULT_PAYMENT_STATUSES[number];
export type DefaultInventoryStatus = typeof DEFAULT_INVENTORY_STATUSES[number];
export type DefaultInventoryCondition = typeof DEFAULT_INVENTORY_CONDITIONS[number];
